import { isObject } from 'lodash';
import { FetchStatus, MutationStatus, QueryStatus } from '@tanstack/react-query';

export enum RequestStatus {
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  INITIAL = 'INITIAL',
}

export function reactQueryMutationStatusToRequestStatus(mutationStatus: MutationStatus): RequestStatus {
  switch (mutationStatus) {
    case 'idle':
      return RequestStatus.INITIAL;
    case 'pending':
      return RequestStatus.LOADING;
    case 'error':
      // OA 5/2022: Our own "RequestStatus" is "SUCCESS" always when we get a response from the server, even if that
      // response has a HTTP status code other than 2**. So the 'error' from React Query is actually "RequestStatus.SUCCESS" in our legacy
      // api implementation, and hence this mapping will give some non-coherent results. We'll need to fix this properly at a later date.
      // In the meanwhile, let's always use React Query.
      return RequestStatus.ERROR;
    case 'success':
      return RequestStatus.SUCCESS;
    default:
      throw Error(`Unknown mutation status ${JSON.stringify(mutationStatus)}`);
  }
}

// Useful for combining react-query systems with older RequestStatus components
export function reactQueryStatusToRequestStatus({
  status,
  fetchStatus,
}: {
  status: QueryStatus;
  fetchStatus: FetchStatus;
}): RequestStatus {
  // Tanstack Query V4 onward, when dependent queries become enabled, the query status is "loading" and the fetch status is "fetching".
  const queryInFlight = fetchStatus === 'fetching' && status === 'pending';
  if (queryInFlight) {
    return RequestStatus.LOADING;
  }

  if (status === 'pending') {
    return RequestStatus.INITIAL;
  }

  if (status === 'error') {
    // OA 5/2022: Our own "RequestStatus" is "SUCCESS" always when we get a response from the server, even if that
    // response has a HTTP status code other than 2**. So the 'error' from React Query is actually "RequestStatus.SUCCESS" in our legacy
    // api implementation, and hence this mapping will give some non-coherent results. We'll need to fix this properly at a later date.
    // In the meanwhile, let's always use React Query.
    return RequestStatus.ERROR;
  }

  return RequestStatus.SUCCESS;
}

export interface ApiResponse<T> {
  data?: T;
  status: number; // we use statuscode 0 if fetch throws
  statusText?: string;
}

export enum ApiTarget {
  SERVER,
  PUBLIC_API_V1,
}

function objectHasProperty<K extends PropertyKey>(object: object, key: K): object is { message: unknown } {
  return key in object;
}

export function getErrorMessage(value: unknown): string | undefined {
  if (isObject(value) && objectHasProperty(value, 'message')) {
    if (typeof value.message === 'string' && value.message.length > 0) {
      return value.message;
    }
  } else if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return undefined;
}
